<div class="custom-domains-page-container">
  @if (initialLoadFailed() && loadError(); as error) {
    <tf-settings-load-error
      [title]="error"
      [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
      [details]="loadErrorDetails()"
      [retrying]="isLoading()"
      (retry)="loadDomains()" />
  } @else if (!hasLoadedOnce()) {
    <tf-settings-loading-placeholder [rows]="4" [columns]="columns().length" />
  } @else if (showEmptyState()) {
    <tf-settings-inline-message
      [message]="actionError()"
      messageId="custom-domains-action-error"
      (dismissed)="clearActionError()" />

    <tf-empty-state
      [title]="'SETTINGS_CUSTOM_DOMAINS.EMPTY.TITLE' | translate"
      [body]="'SETTINGS_CUSTOM_DOMAINS.EMPTY.BODY' | translate">
      <svg
        empty-state-illustration
        class="settings-empty-illustration custom-domains-empty-illustration"
        width="96"
        height="96"
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true">
        <circle class="custom-domains-empty-illustration__globe" cx="48" cy="45" r="27" />
        <path
          class="custom-domains-empty-illustration__grid"
          d="M21 45H75M48 18C56 26 60 35 60 45S56 64 48 72M48 18C40 26 36 35 36 45S40 64 48 72M28 29C33 33 40 35 48 35S63 33 68 29M28 61C33 57 40 55 48 55S63 57 68 61" />
        <rect
          class="custom-domains-empty-illustration__record"
          x="48"
          y="59"
          width="35"
          height="18"
          rx="4" />
        <path class="custom-domains-empty-illustration__record-line" d="M56 68H75" />
        <path
          class="custom-domains-empty-illustration__link"
          d="M72 32L82 32C86 32 89 35 89 39S86 46 82 46H74M78 39H65C61 39 58 42 58 46S61 53 65 53H73" />
      </svg>
      <p-button
        empty-state-actions
        icon="pi pi-plus"
        [label]="'SETTINGS_CUSTOM_DOMAINS.ADD_DOMAIN' | translate"
        [disabled]="!canCreateDomain()"
        [pTooltip]="canCreateDomain() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="openAddWizard()" />
      <p-button
        empty-state-actions
        icon="pi pi-refresh"
        severity="secondary"
        [loading]="isLoading()"
        [disabled]="isLoading()"
        [ariaLabel]="'SETTINGS_CUSTOM_DOMAINS.RELOAD' | translate"
        (onClick)="loadDomains()" />
    </tf-empty-state>
  } @else {
    <tf-settings-list-header
      [showSearch]="false"
      [loading]="isLoading()"
      [refreshTooltip]="'SETTINGS_CUSTOM_DOMAINS.RELOAD' | translate"
      (refresh)="loadDomains()">
      <p-button
        headerActions
        icon="pi pi-plus"
        severity="primary"
        [label]="'SETTINGS_CUSTOM_DOMAINS.ADD_DOMAIN' | translate"
        [disabled]="!canCreateDomain()"
        [pTooltip]="canCreateDomain() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="openAddWizard()" />
    </tf-settings-list-header>

    <tf-settings-inline-message
      [message]="actionError()"
      messageId="custom-domains-action-error"
      (dismissed)="clearActionError()" />

    <tf-data-table
      [columns]="columns()"
      [value]="domains()"
      [loading]="false"
      emptyMessageKey="SETTINGS_CUSTOM_DOMAINS.NO_DOMAINS_FOUND">
      <ng-template tfColumnDef="domain" let-row>
        <span class="domain-name">{{ row.domain }}</span>
      </ng-template>
      <ng-template tfColumnDef="status" let-row>
        <p-tag [severity]="statusSeverity(row.status)" [value]="statusLabel(row.status)" />
      </ng-template>
      <ng-template tfColumnDef="_brand" let-row>
        @if (brandFor(row.domain); as brand) {
          <span
            class="brand-chip"
            [pTooltip]="'SETTINGS_CUSTOM_DOMAINS.BRAND_HINT' | translate"
            tooltipPosition="top">
            @if (brand.logoUrl) {
              <img class="brand-chip-logo" [src]="brand.logoUrl" alt="" loading="lazy" />
            }
            <span class="brand-chip-name">{{ brand.name }}</span>
          </span>
        } @else {
          <span class="brand-empty">
            {{ 'SETTINGS_CUSTOM_DOMAINS.BRAND_UNLINKED' | translate }}
          </span>
        }
      </ng-template>
      <ng-template tfColumnDef="verifiedAt" let-row>
        @if (row.verifiedAt) {
          <span class="verified-at">{{ row.verifiedAt | date: 'mediumDate' }}</span>
        } @else {
          <span class="brand-empty">
            {{ 'SETTINGS_CUSTOM_DOMAINS.BRAND_UNLINKED' | translate }}
          </span>
        }
      </ng-template>
      <ng-template tfColumnDef="_actions" let-row>
        <div class="action-buttons">
          <p-button
            icon="pi pi-trash"
            severity="danger"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canDeleteDomain()"
            [pTooltip]="
              canDeleteDomain()
                ? ('SETTINGS_CUSTOM_DOMAINS.ACTIONS.REMOVE' | translate)
                : ('GENERAL.NO_PERMISSION' | translate)
            "
            tooltipPosition="top"
            [attr.aria-label]="'SETTINGS_CUSTOM_DOMAINS.ACTIONS.REMOVE' | translate"
            (onClick)="confirmRemove(row)" />
          @if (inlineAction(row); as action) {
            <p-button
              [label]="action.label"
              size="small"
              severity="secondary"
              [disabled]="!canUpdateDomain()"
              [pTooltip]="canUpdateDomain() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
              tooltipPosition="top"
              (onClick)="action.handler()" />
          }
        </div>
      </ng-template>
    </tf-data-table>
  }

  <p-confirmDialog />

  @if (wizardVisible()) {
    <tf-settings-add-custom-domain-dialog
      [initialDomain]="wizardInitialDomain()"
      [initialStep]="wizardInitialStep()"
      (closed)="onWizardClosed()"
      (created)="onDomainCreated($event)"
      (updated)="onDomainUpdated($event)" />
  }
</div>
